/* eslint-disable react/require-optimization */
/* eslint-disable max-lines */

import React from 'react';
import classNames from 'classnames';
import type {IntlShape} from 'react-intl';
import {FormattedMessage, injectIntl} from 'react-intl';

import isEqual from '@tinkoff/utils/is/equal';
import pick from '@tinkoff/utils/object/pick';
import debounce from '@tinkoff/utils/function/debounce';

import {keyBy} from 'lodash';

import type {ModalData} from 'types/actions';

import {sortFileInfos} from 'mattermost-redux/utils/file_utils';

import * as GlobalActions from 'actions/global_actions';

import Constants, {Locations, ModalIdentifiers} from 'utils/constants';
import * as UserAgent from 'utils/user_agent';
import * as Utils from 'utils/utils';
import {
    groupsMentionedInText,
    isErrorInvalidSlashCommand,
    postMessageOnKeyPress,
    shouldFocusMainTextbox,
    specialMentionsInText,
    splitMessageBasedOnCaretPosition,
} from 'utils/post_utils';
import {
    formatGithubCodePaste,
    formatLink,
    formatMarkdownTableMessage,
    getTable,
    isGitHubCodeBlock,
} from 'utils/paste';

import NotifyConfirmModal from 'components/notify_confirm_modal';
import EmojiPickerOverlay from 'components/emoji_picker/emoji_picker_overlay';
import FilePreview from 'components/file_preview';
import FileUpload from 'components/file_upload';
import type {FileUpload as FileUploadClass} from 'components/file_upload/file_upload';
import MsgTyping from 'components/msg_typing';
import PostQuote from 'components/post_view/post_quote';
import PostDeletedModal from 'components/post_deleted_modal';
import EmojiIcon from 'components/widgets/icons/emoji_icon';
import Textbox from 'components/textbox';
import type TextboxClass from 'components/textbox/textbox';
import {CreateCommentMoreButton} from 'components/textbox_more_button';
import {MessageSubmitError} from 'components/message_submit_error';
import type {Group} from 'mattermost-redux/types/groups';
import type {FilePreviewInfo} from 'components/file_preview/file_preview';
import type {Emoji} from 'mattermost-redux/types/emojis';
import type {ActionResult} from 'mattermost-redux/types/actions';
import type {ServerError} from 'mattermost-redux/types/errors';
import type {FileInfo} from 'mattermost-redux/types/files';

import RhsSuggestionList from 'components/suggestion/rhs_suggestion_list';
import Tooltip from '../tooltip';
import OverlayTrigger from '../overlay_trigger';
import {t} from '../../utils/i18n';
import LocalizedIcon from '../localized_icon';
import type {Post} from '@mattermost/types/posts';
import type {Draft, setDraftAction} from 'features/drafts';
import {areDraftsEqual} from 'features/drafts';
import type {PostDraft} from 'types/store/rhs';
import {Client4} from 'mattermost-redux/client';
import {sendToStatist} from '@time-webkit/statist';
import {mark, Marks} from '@time-webkit/performance';
import {asyncResult} from 'utils/async_result';
import {TextboxError} from 'components/textbox/textbox_error';

import styles from './create_comment.module.css';

const KeyCodes = Constants.KeyCodes;

export type Props = {
    teamName: string;

    /**
     * The channel for which this comment is a part of
     */
    channelId: string;

    channelType: string;

    /**
     * The number of channel members
     */
    channelMembersCount: number;

    /**
     * The id of the parent post
     */
    rootId: string;

    /**
     * True if the root message was deleted
     */
    rootDeleted: boolean;

    /**
     * The current history message selected
     */
    messageInHistory?: string;

    /**
     * The current draft of the comment
     */
    draft: Draft;

    /**
     * Whether the submit button is enabled
     */
    enableAddButton?: boolean;

    /**
     * Force message submission on CTRL/CMD + ENTER
     */
    codeBlockOnCtrlEnter?: boolean;

    /**
     * Set to force form submission on CTRL/CMD + ENTER instead of ENTER
     */
    ctrlSend?: boolean;

    /**
     * The id of the latest post in this channel
     */
    latestPostId?: string;
    locale: string;

    /**
     * Create post error id
     */
    createPostErrorId?: string;

    /**
     * Called to clear file uploads in progress
     */
    // clearCommentDraftUploads: () => void;

    intl: IntlShape;

    /**
     * Called when comment draft needs to be updated for an specific root ID
     */
    setDraft: typeof setDraftAction;

    /**
     * Called when submitting the comment
     */
    onSubmit: (draft: PostDraft, options: {ignoreSlash: boolean}) => void;

    /**
     * Called when resetting comment message history index
     */
    onResetHistoryIndex: () => void;

    /**
     * Called when navigating back through comment message history
     */
    onMoveHistoryIndexBack: () => void;

    /**
     * Called when navigating forward through comment message history
     */
    onMoveHistoryIndexForward: () => void;

    /**
     * Called to initiate editing the user's latest post
     */
    onEditLatestPost: () => ActionResult;

    /**
     * Function to get the users timezones in the channel
     */
    getChannelTimezones: (channelId: string) => Promise<ActionResult>;

    /**
     * Reset state of createPost request
     */
    resetCreatePostRequest: () => void;

    /**
     * Set if @channel should warn in this channel.
     */
    enableConfirmNotificationsToChannel: boolean;

    /**
     * Set if the emoji picker is enabled.
     */
    enableEmojiPicker: boolean;

    /**
     * Set if the connection may be bad to warn user
     */
    badConnection: boolean;

    /**
     * The maximum length of a post
     */
    maxPostSize: number;
    rhsExpanded: boolean;

    /**
     * To check if the timezones are enable on the server.
     */
    isTimezoneEnabled: boolean;

    /**
     * The last time, if any, when the selected post changed. Will be 0 if no post selected.
     */
    selectedPostFocussedAt: number;

    /**
     * Function to set or unset emoji picker for last message
     */
    emitShortcutReactToLastPostFrom: (location: string) => void;

    canPost: boolean;

    /**
     * To determine if the current user can send special channel mentions
     */
    useChannelMentions: boolean;

    /**
     * To determine if the current user can send LDAP group mentions
     */
    useLDAPGroupMentions: boolean;

    /**
     * Set show preview for textbox
     */
    setShowPreview: (showPreview: boolean) => void;

    /**
     * Should preview be showed
     */
    shouldShowPreview: boolean;

    /***
     * Called when parent component should be scrolled to bottom
     */
    scrollToBottom?: () => void;
    groupsWithAllowReference: Map<string, Group> | null;
    onHeightChange?: (height: number, maxHeight: number) => void;
    focusOnMount?: boolean;
    isThreadView?: boolean;
    openModal: <P>(modalData: ModalData<P>) => void;
    useCustomGroupMentions: boolean;
    onReplyHandle?: () => void;
    isEditing: boolean;
    unsetEditingPost: () => void;
};

type State = {
    showEmojiPicker: boolean;
    uploadsProgressPercent: {[clientID: string]: FilePreviewInfo};
    renderScrollbar: boolean;
    scrollbarWidth: number;
    messageInHistory?: string;
    createPostErrorId?: string;
    caretPosition: number;
    postError?: React.ReactNode;
    serverError: (ServerError & {submittedMessage?: string}) | null;
    clientUploadError: Error | null;
    message: string;
    fileInfos: FileInfo[];
    uploadsInProgress: string[];
    quote?: Post;
    submitting: boolean;
};

class CreateComment extends React.Component<Props, State> {
    private lastBlurAt = 0;
    private doInitialScrollToBottom = false;
    private focusedOsMount = false;

    private textboxRef = React.createRef<TextboxClass>();
    private fileUploadRef = React.createRef<FileUploadClass>();
    private createCommentControlsRef = React.createRef<HTMLSpanElement>();

    static defaultProps = {
        focusOnMount: true,
    };

    state = {
        showEmojiPicker: false,
        uploadsProgressPercent: {},
        renderScrollbar: false,
        scrollbarWidth: 0,
        serverError: null,
        clientUploadError: null,
        message: '',
        fileInfos: [],
        caretPosition: 0,
        uploadsInProgress: [],
        submitting: false,
    } as State;

    private isMounted = false;

    private setInitialStateFromDraft() {
        this.setState({
            fileInfos: this.props.draft.fileInfos || [],
            message: this.props.draft.message || '',
            caretPosition: this.props.draft.message.length || 0,
            uploadsInProgress: [],
            quote: this.props.draft.quote,
        });
    }

    componentDidMount() {
        const {
            onResetHistoryIndex,
            setShowPreview,
            draft,
        } = this.props;

        // @TODO: разобраться чего делает
        // this.props.clearCommentDraftUploads();
        onResetHistoryIndex();
        setShowPreview(false);

        this.setInitialStateFromDraft();

        if (this.props.focusOnMount) {
            this.focusTextbox();
        }

        document.addEventListener('paste', this.pasteHandler);
        document.addEventListener('keydown', this.focusTextboxIfNecessary);
        window.addEventListener('beforeunload', this.saveDraft);
        window.addEventListener('message', this.handleMessageEvent);

        // When draft.message is not empty, set doInitialScrollToBottom to true so that
        // on next component update, the actual this.scrollToBottom() will be called.
        // This is made so that the this.scrollToBottom() will be called only once.
        if (draft.message !== '') {
            this.doInitialScrollToBottom = true;
        }

        this.isMounted = true;
    }

    componentWillUnmount() {
        this.isMounted = false;

        const {uploadsInProgress} = this.state;
        uploadsInProgress?.forEach((id) => this.fileUploadRef.current?.cancelUpload(id));

        this.props.resetCreatePostRequest?.();
        document.removeEventListener('paste', this.pasteHandler);
        document.removeEventListener('keydown', this.focusTextboxIfNecessary);
        window.removeEventListener('beforeunload', this.saveDraft);
        window.removeEventListener('message', this.handleMessageEvent);
        this.saveDraft();

        if (this.props.isEditing) {
            this.props.unsetEditingPost();
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const {uploadsInProgress, showEmojiPicker} = this.state;
        const {
            shouldShowPreview,
            focusOnMount,
            selectedPostFocussedAt,
            scrollToBottom,
            createPostErrorId,
        } = this.props;

        if (prevState.uploadsInProgress.length !== uploadsInProgress.length) {
            scrollToBottom?.();
        }

        // Focus on textbox when emoji picker is closed
        if (prevState.showEmojiPicker && !showEmojiPicker) {
            this.focusTextbox();
        }

        // Focus on textbox when returned from preview mode
        if (prevProps.shouldShowPreview && !shouldShowPreview) {
            this.focusTextbox();
        }

        // focusOnMount will change on scroll to bottom
        if (!this.focusedOsMount && !prevProps.focusOnMount && focusOnMount) {
            this.focusedOsMount = true;
            this.focusTextbox();
        }

        if (prevProps.selectedPostFocussedAt !== selectedPostFocussedAt) {
            this.focusTextbox();
        }

        if (this.doInitialScrollToBottom) {
            scrollToBottom?.();
            this.doInitialScrollToBottom = false;
        }

        if (
            createPostErrorId === 'api.post.create_post.root_id.app_error' &&
            createPostErrorId !== prevProps.createPostErrorId
        ) {
            this.showPostDeletedModal();
        }

        if (!areDraftsEqual(prevState as any, this.state as any)) {
            this.saveDraftDebounced();
        }
    }

    get isTextboxFocused() {
        if (!this.textboxRef.current) {
            return false;
        }

        return this.textboxRef.current.getInputBox() === document.activeElement;
    }

    saveDraft = async () => {
        if (!this.props || !this.state || !this.isMounted) {
            return;
        }

        const {rootId, draft, setDraft} = this.props;
        const {fileInfos, message, quote, submitting} = this.state;

        if (submitting) {
            return;
        }

        if (
            isEqual(pick(['fileInfos', 'message', 'quote'], draft), {
                fileInfos,
                message,
                quote,
            })
        ) {
            return;
        }

        if (rootId && (this.isTextboxFocused || draft.quote !== quote)) {
            if (!message && !fileInfos?.length) {
                this.clearThreadDraft();
                return;
            }

            setDraft({
                draft: {
                    fileInfos,
                    message,
                    quote,
                },
                id: rootId,
                isThread: true,
            });
        }
    };

    saveDraftDebounced = debounce(1000, () => {
        if (!this.isMounted) {
            return;
        }

        this.saveDraft();
    });

    setShowPreview = (newPreviewValue: boolean) => this.props.setShowPreview(newPreviewValue);

    focusTextboxIfNecessary = (e: KeyboardEvent) => {
        const {isThreadView, rhsExpanded} = this.props;

        // Should only focus if RHS is expanded or if thread view
        if (!isThreadView && !rhsExpanded) {
            return;
        }

        // Bit of a hack to not steal focus from the channel switch modal if it's open
        // This is a special case as the channel switch modal does not enforce focus like
        // most modals do
        if (document.getElementsByClassName('channel-switch-modal').length) {
            return;
        }

        if (shouldFocusMainTextbox(e, document.activeElement)) {
            this.focusTextbox();
        }
    };

    setCaretPosition = (newCaretPosition: number) => {
        const textbox = this.textboxRef.current && this.textboxRef.current.getInputBox();

        this.setState(
            {
                caretPosition: newCaretPosition,
            },
            () => Utils.setCaretPosition(textbox, newCaretPosition),
        );
    };

    setCaretPositionAndMessage = (newMessage: string, newCaretPosition: number) => {
        const textbox = this.textboxRef.current && this.textboxRef.current.getInputBox();

        this.setState(
            {
                caretPosition: newCaretPosition,
                message: newMessage,
            },
            () => Utils.setCaretPosition(textbox, newCaretPosition),
        );
    };

    pasteHandler = (e: ClipboardEvent) => {
        if (!e.clipboardData || !e.clipboardData.items || (e.target as any).id !== 'reply_textbox') {
            return;
        }

        const {clipboardData} = e;

        const {message, caretPosition} = this.state;
        const formattedLink = formatLink(clipboardData, message, caretPosition);
        if (formattedLink) {
            this.setCaretPositionAndMessage(formattedLink.newMessage, formattedLink.newCaretPosition);
            e.preventDefault();
            return;
        }

        let table = getTable(clipboardData);
        if (!table) {
            return;
        }

        table = table as HTMLTableElement;

        e.preventDefault();

        let newMessage = message;

        if (isGitHubCodeBlock(table.className)) {
            const {formattedMessage, formattedCodeBlock} = formatGithubCodePaste(
                caretPosition,
                newMessage,
                clipboardData,
            );
            const newCaretPosition = caretPosition + formattedCodeBlock.length;

            newMessage = formattedMessage;

            this.setCaretPositionAndMessage(newMessage, newCaretPosition);
        } else {
            const originalSize = message.length;
            newMessage = formatMarkdownTableMessage(table, message.trim(), caretPosition);

            const newCaretPosition = newMessage.length - (originalSize - caretPosition);

            this.setCaretPositionAndMessage(newMessage, newCaretPosition);
        }
    };

    // @TODO: переписать передачу postProps на более элегантную
    handleNotifyAllConfirmation = (postProps: Post['props']) => this.doSubmit(undefined, postProps);

    showNotifyAllModal = (
        mentions: string[],
        channelTimezoneCount: number,
        memberNotifyCount: number,
        postProps: Post['props'],
    ) => {
        this.props.openModal({
            modalId: ModalIdentifiers.NOTIFY_CONFIRM_MODAL,
            dialogType: NotifyConfirmModal,
            dialogProps: {
                mentions,
                channelTimezoneCount,
                memberNotifyCount,
                onConfirm: () => this.handleNotifyAllConfirmation(postProps),
            },
        });
    };

    toggleEmojiPicker = () => {
        if (!this.state.showEmojiPicker) {
            sendToStatist('searchEmoji.tap', {
                sourceTap: 'thread',
            });
        }
        this.setState((prevState) => ({...prevState, showEmojiPicker: !prevState.showEmojiPicker}));
    }

    hideEmojiPicker = () => this.setState({showEmojiPicker: false});

    handleEmojiClick = (emoji: Emoji) => {
        const emojiAlias = ('short_name' in emoji && emoji.short_name) || emoji.name;

        if (!emojiAlias) {
            //Oops.. There went something wrong
            return;
        }

        let newMessage: string;

        const {message} = this.state;

        if (message === '') {
            newMessage = `:${emojiAlias}: `;

            this.setCaretPositionAndMessage(newMessage, newMessage.length);
        } else {
            const {firstPiece, lastPiece} = splitMessageBasedOnCaretPosition(this.state.caretPosition || 0, message);

            // check whether the first piece of the message is empty when cursor is placed at beginning of message
            // and avoid adding an empty string at the beginning of the message
            newMessage =
                firstPiece === '' ? `:${emojiAlias}: ${lastPiece} ` : `${firstPiece} :${emojiAlias}: ${lastPiece} `;

            const newCaretPosition =
                firstPiece === '' ? `:${emojiAlias}: `.length : `${firstPiece} :${emojiAlias}: `.length;

            this.setCaretPositionAndMessage(newMessage, newCaretPosition);
        }

        this.setState({
            showEmojiPicker: false,
        });
    };

    handlePostError = (postError: React.ReactNode) => this.setState({postError});

    handleSubmit = async (e: React.FormEvent | React.MouseEvent) => {
        e.preventDefault();
        this.setShowPreview(false);
        this.setState({
            submitting: true,
        });

        const {
            channelMembersCount,
            enableConfirmNotificationsToChannel,
            useChannelMentions,
            isTimezoneEnabled,
            groupsWithAllowReference,
            useLDAPGroupMentions,
            useCustomGroupMentions,
            channelId,
            getChannelTimezones,
        } = this.props;

        const {message} = this.state;

        const notificationsToChannel = enableConfirmNotificationsToChannel && useChannelMentions;
        let memberNotifyCount = 0;
        let channelTimezoneCount = 0;
        let mentions: string[] = [];

        const specialMentions = specialMentionsInText(message);

        const hasSpecialMentions = Object.values(specialMentions).includes(true);

        this.setShowPreview(false);

        const postProps: Post['props'] = {};

        if (
            !hasSpecialMentions &&
            (useLDAPGroupMentions || useCustomGroupMentions)
        ) {
            // Groups mentioned in users text
            const mentionGroups = groupsMentionedInText(message, groupsWithAllowReference);

            if (mentionGroups.length > 0) {
                const [channelGroupsMembersCount] = await asyncResult(
                    Client4.getChannelMemberCountsByGroupIDs(channelId, mentionGroups.map((group) => group.id), true),
                );
                const channelGroupsMembersCountDict = keyBy(channelGroupsMembersCount, (el) => el.group_id);
                mentionGroups.forEach((group) => {
                    if (group.source === 'ldap' && !useLDAPGroupMentions) {
                        return;
                    }
                    if (group.source === 'custom' && !useCustomGroupMentions) {
                        return;
                    }
                    const mappedValue = channelGroupsMembersCountDict[group.id];
                    if (mappedValue) {
                        memberNotifyCount += mappedValue.channel_member_count;
                        channelTimezoneCount += mappedValue.channel_member_timezones_count;
                    }
                    mentions.push(`@${group.name}`);
                });
                mentions = [...new Set(mentions)];
            }
        }

        if (!useLDAPGroupMentions && !useCustomGroupMentions && mentions.length > 0) {
            postProps.disable_group_highlight = true;
        }

        if (hasSpecialMentions) {
            memberNotifyCount = channelMembersCount;
            for (const k in specialMentions) {
                if (specialMentions[k] === true) {
                    mentions.push('@' + k);
                }
            }

            if (isTimezoneEnabled) {
                const {data} = await getChannelTimezones(channelId);
                channelTimezoneCount = data ? data.length : 0;
            }
        }

        if (!useChannelMentions && hasSpecialMentions) {
            postProps.mentionHighlightDisabled = true;
        }

        if (notificationsToChannel && memberNotifyCount > Constants.NOTIFY_ALL_MEMBERS) {
            this.showNotifyAllModal(mentions, channelTimezoneCount, memberNotifyCount, postProps);
            return;
        }

        await this.doSubmit(e, postProps);
    };

    doSubmit = async (e?: React.FormEvent, postProps: Post['props'] = {}) => {
        if (e) {
            e.preventDefault();
        }

        const {postError, uploadsInProgress, serverError, message, fileInfos, quote} = this.state;
        const {rootDeleted} = this.props;

        const enableAddButton = this.shouldEnableAddButton();

        if (!enableAddButton) {
            this.setState({
                submitting: false,
            });
            return;
        }

        if (uploadsInProgress.length > 0) {
            this.setState({
                submitting: false,
            });
            return;
        }

        if (postError) {
            this.setState({
                submitting: false,
            });
            return;
        }

        if (rootDeleted) {
            this.showPostDeletedModal();

            this.setState({
                submitting: false,
            });
            return;
        }

        const fasterThanHumanWillClick = 150;
        const forceFocus = Date.now() - this.lastBlurAt < fasterThanHumanWillClick;

        this.focusTextbox(forceFocus);

        let ignoreSlash = false;

        if (isErrorInvalidSlashCommand(serverError) && message === serverError?.submittedMessage) {
            ignoreSlash = true;
        }

        const options = {ignoreSlash};

        mark(Marks.MESSAGE_SENT, {
            detail: {
                isThread: true,
                channelId: this.props.channelId,
            },
        });
        try {
            mark(Marks.MESSAGE_LOADING_STARTED);
            await this.props.onSubmit(
                {
                    props: postProps,
                    message,
                    fileInfos,
                    quote,
                },
                options,
            );

            this.setState({
                postError: null,
                serverError: null,
                clientUploadError: null,
            });
        } catch (err: any) {
            if (isErrorInvalidSlashCommand(err)) {
                this.props.setDraft({
                    draft: {
                        message,
                        fileInfos,
                        quote,
                    } as any,
                    id: this.props.rootId,
                    isThread: true,
                });
            }

            err.submittedMessage = message;

            this.setState({serverError: err, clientUploadError: null, submitting: false});
            return;
        }
        mark(Marks.MESSAGE_LOADING_FINISHED);
        await this.clearThreadDraft();

        this.setState({
            uploadsInProgress: [],
            message: '',
            caretPosition: 0,
            fileInfos: [],
            quote: undefined,
            submitting: false,
        });
    };

    clearThreadDraft = async () => {
        const {rootId, setDraft} = this.props;
        setDraft({
            draft: null,
            id: rootId,
            isThread: true,
        });
        return true;
    };

    commentMsgKeyPress = (e: React.KeyboardEvent) => {
        const {ctrlSend, codeBlockOnCtrlEnter} = this.props;
        const {message} = this.state;

        const {
            allowSending,
            withClosedCodeBlock,
            message: newMessage,
        } = postMessageOnKeyPress(
            e,
            message,
            Boolean(ctrlSend),
            Boolean(codeBlockOnCtrlEnter),
            0,
            0,
            this.state.caretPosition,
        ) as {
            allowSending: boolean;
            withClosedCodeBlock?: boolean;
            message?: string;
        };

        if (allowSending) {
            if (this.textboxRef.current) {
                this.textboxRef.current.blur();
            }

            if (withClosedCodeBlock && newMessage) {
                this.setState({message: newMessage}, () => this.handleSubmit(e));
            } else {
                this.handleSubmit(e);
            }

            this.setShowPreview(false);

            window.requestAnimationFrame(() => {
                this.focusTextbox();
            });
        }

        this.emitTypingEvent();
    };

    reactToLastMessage = (e: React.KeyboardEvent) => {
        e.preventDefault();

        const {emitShortcutReactToLastPostFrom} = this.props;

        // Here we are not handling conditions such as check for modals,  popups etc as shortcut is only trigger on
        // textbox input focus. Since all of them will already be closed as soon as they loose focus.
        emitShortcutReactToLastPostFrom(Locations.RHS_ROOT);
    };

    applyAutocorrect = (event: any) => {
        const {message, caretPosition} = this.state;

        const {firstPiece, lastPiece} = splitMessageBasedOnCaretPosition(caretPosition, message);

        if (event.key === ' ') {
            const textBefore = firstPiece.slice(-3);
            if (textBefore === ' --' || textBefore === '\n--' || textBefore === '--') {
                const firstPieceCut = firstPiece.slice(0, -2);
                const newMessage = `${firstPieceCut}—${lastPiece}`;
                const newCaretPosition = firstPieceCut.length + 1;

                this.setCaretPositionAndMessage(newMessage, newCaretPosition);
            }
        }
    };

    emitTypingEvent = () => {
        const {channelId, rootId} = this.props;
        GlobalActions.emitLocalUserTypingEvent(channelId, rootId);
    };

    handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const message = e.target.value;

        let serverError = this.state.serverError;

        if (isErrorInvalidSlashCommand(serverError)) {
            serverError = null;
        }

        // TIME-13832 костыль для корректного изменения позиции каретки после автоселекта логина
        if (e.hasOwnProperty('currentCaretPosition')) {
            this.setState({
                message,
                caretPosition: (e as any).currentCaretPosition || 0,
                serverError,
            });
            return;
        }

        this.setState({message, serverError}, () => this.props.scrollToBottom?.());
    };

    handleMouseUpKeyUp = (e: React.MouseEvent | React.KeyboardEvent) => {
        const caretPosition = Utils.getCaretPosition(e.target as HTMLElement);
        this.setState({
            caretPosition,
        });
    };

    handleSelect = (e: React.SyntheticEvent) => {
        Utils.adjustSelection(this.textboxRef.current?.getInputBox(), e);
    };

    handleKeyDown = (e: React.KeyboardEvent) => {
        const ctrlOrMetaKeyPressed = e.ctrlKey || e.metaKey;
        const lastMessageReactionKeyCombo =
            ctrlOrMetaKeyPressed && e.shiftKey && Utils.isKeyPressed(e, KeyCodes.BACK_SLASH);

        // listen for line break key combo and insert new line character
        if (Utils.isUnhandledLineBreakKeyCombo(e)) {
            this.setState({
                message: Utils.insertLineBreakFromKeyEvent(e),
            });
            return;
        }

        if (
            (this.props.ctrlSend || this.props.codeBlockOnCtrlEnter) &&
            Utils.isKeyPressed(e, Constants.KeyCodes.ENTER) &&
            (e.ctrlKey || e.metaKey)
        ) {
            this.setShowPreview(false);
            this.commentMsgKeyPress(e);
            return;
        }

        const {message} = this.state;

        if (Utils.isKeyPressed(e, Constants.KeyCodes.ESCAPE)) {
            if (this.textboxRef.current) {
                this.textboxRef.current.blur();
            }
        }

        if (
            !e.ctrlKey &&
            !e.metaKey &&
            !e.altKey &&
            !e.shiftKey &&
            Utils.isKeyPressed(e, Constants.KeyCodes.UP) &&
            message === ''
        ) {
            e.preventDefault();
            if (this.textboxRef.current) {
                this.textboxRef.current.blur();
            }

            const {data: canEditNow} = this.props.onEditLatestPost();

            if (!canEditNow) {
                this.focusTextbox(true);
            }
        }

        const ctrlKeyCombo = Utils.cmdOrCtrlPressed(e) && !e.altKey && !e.shiftKey;
        const ctrlAltCombo = Utils.cmdOrCtrlPressed(e, true) && e.altKey;

        if (ctrlKeyCombo) {
            if (Utils.isKeyPressed(e, Constants.KeyCodes.UP)) {
                e.preventDefault();
                this.props.onMoveHistoryIndexBack();
            } else if (Utils.isKeyPressed(e, Constants.KeyCodes.DOWN)) {
                e.preventDefault();
                this.props.onMoveHistoryIndexForward();
            } else if (Utils.isKeyPressed(e, Constants.KeyCodes.B) || Utils.isKeyPressed(e, Constants.KeyCodes.I)) {
                this.applyHotkeyMarkdown(e);
            }
        }

        if (ctrlAltCombo && Utils.isKeyPressed(e, Constants.KeyCodes.K)) {
            this.applyHotkeyMarkdown(e);
        }

        if (lastMessageReactionKeyCombo) {
            this.reactToLastMessage(e);
        } else {
            this.applyAutocorrect(e);
        }
    };

    applyHotkeyMarkdown = (e: React.KeyboardEvent) => {
        const res = Utils.applyHotkeyMarkdown(e);

        this.setState(
            {
                message: res.message,
            },
            () => {
                const textbox = this.textboxRef.current?.getInputBox();
                Utils.setSelectionRange(textbox, res.selectionStart, res.selectionEnd);
            },
        );
    };

    handleFileUploadChange = () => {
        this.focusTextbox();
    };

    handleUploadStart = (clientIds: string[]) => {
        this.setState(({uploadsInProgress}) => ({uploadsInProgress: [...uploadsInProgress, ...clientIds]}), () => {
            // this is a bit redundant with the code that sets focus when the file input is clicked,
            // but this also resets the focus after a drag and drop
            this.focusTextbox();

            this.props.scrollToBottom?.();
        });
    };

    handleUploadProgress = (filePreviewInfo: FilePreviewInfo) => {
        this.setState(({uploadsProgressPercent}) => ({
            uploadsProgressPercent: {
                ...uploadsProgressPercent,
                [filePreviewInfo.clientId]: filePreviewInfo,
            },
        }));
    };

    handleFileUploadComplete = (fileInfos: FileInfo[], clientIds: string[], channelId: string, rootId?: string) => {
        const {locale, scrollToBottom, rootId: propsRootId} = this.props;

        if (propsRootId === rootId) {
            this.setState((prevState) => ({
                uploadsInProgress: prevState.uploadsInProgress.filter((uploadInProgress) => !clientIds.includes(uploadInProgress)),
                fileInfos: sortFileInfos(prevState.fileInfos.concat(fileInfos), locale),
            }),
            () => scrollToBottom?.(),
            );
        }
    };

    handleUploadServerError = (
        err: string | ServerError | null,
        clientId: string | number = -1,
        currentChannelId?: string,
        rootId = '',
    ) => {
        if (clientId !== -1) {
            const {uploadsInProgress} = this.state;
            const newUploadsInProgress = uploadsInProgress.filter((id) => id !== clientId);

            if (this.props.rootId === rootId) {
                this.setState({uploadsInProgress: newUploadsInProgress});
            }
        }

        let serverError = err;
        if (typeof serverError === 'string') {
            serverError = new Error(serverError);
        }

        this.setState({serverError}, () => {
            if (serverError) {
                this.props.scrollToBottom?.();
            }
        });
    };

    handleUploadClientError = (error: Error | null) => {
        if (!this.state.clientUploadError && !error) {
            return;
        }

        this.setState({clientUploadError: error}, () => {
            if (error) {
                this.props.scrollToBottom?.();
            }
        });
    }

    removePreview = (removedFileId: string) => {
        const {fileInfos, uploadsInProgress} = this.state;
        const newFileInfos = fileInfos.filter(({id}) => id !== removedFileId);
        const newUploadsInProgress = uploadsInProgress.filter((id) => id !== removedFileId);

        // Clear previous errors
        this.setState({
            serverError: null,
            clientUploadError: null,
        });

        // id can either be the id of an uploaded file or the client id of an in progress upload
        if (uploadsInProgress.includes(removedFileId) && this.fileUploadRef.current) {
            this.fileUploadRef.current?.cancelUpload(removedFileId);
        }

        this.setState({fileInfos: newFileInfos, uploadsInProgress: newUploadsInProgress}, () =>
            this.handleFileUploadChange(),
        );
    };

    getFileCount = () => {
        const {fileInfos, uploadsInProgress} = this.state;

        return fileInfos.length + uploadsInProgress.length;
    };

    getFileUploadTarget = () => {
        return this.textboxRef.current?.getInputBox?.() || null;
    };

    getCreateCommentControls = () => {
        return this.createCommentControlsRef.current;
    };

    focusTextbox = (keepFocus = false) => {
        if (this.textboxRef.current && (keepFocus || !UserAgent.isMobile())) {
            this.textboxRef.current.focus();
        }
    };

    shouldEnableAddButton = () => {
        const {message, fileInfos, serverError} = this.state;

        if (message.trim().length !== 0 || fileInfos.length !== 0) {
            return true;
        }

        return isErrorInvalidSlashCommand(serverError);
    };

    showPostDeletedModal = () => {
        this.props.openModal({
            modalId: ModalIdentifiers.POST_DELETED_MODAL,
            dialogType: PostDeletedModal,
        });
    };

    handleBlur = () => {
        this.lastBlurAt = Date.now();
    };

    handlePostQuoteClose = () => this.setState({quote: undefined});

    handleHeightChange = (height: number, maxHeight: number) => {
        this.setState({renderScrollbar: height > maxHeight});

        window.requestAnimationFrame(() => {
            if (this.textboxRef.current) {
                const newScrollbarWidth = Utils.scrollbarWidth(this.textboxRef.current.getInputBox());

                this.setState({scrollbarWidth: newScrollbarWidth});
            }
        });

        if (this.props.onHeightChange) {
            this.props.onHeightChange(height, maxHeight);
        }
    };

    handleMessageEvent = (event: MessageEvent) => {
        if (!event.data || event.origin !== window.location.origin) {
            return;
        }

        const postInCurrentThread = event.data.post?.root_id === this.props.rootId;
        const postIsRootMessage = event.data.post?.id === this.props.rootId;

        const isReplyToPostEvent =
            event.data.type === 'reply_to_message' &&
            (event.data.location === Locations.RHS_COMMENT || event.data.location === Locations.RHS_ROOT) &&
            (!this.props.rootId || postInCurrentThread || postIsRootMessage);

        if (isReplyToPostEvent) {
            const post = event.data.post as Post;

            this.setState({quote: post}, () => this.focusTextbox());
            this.props.onReplyHandle?.();
        }

        if (event.data.type === 'post_deleted') {
            const postId: string = event.data.postId;

            if (this.state.quote?.id === postId) {
                this.handlePostQuoteClose();
            }
        }
    };

    render() {
        const {
            canPost,
            rootId,
            channelId,
            shouldShowPreview,
            isThreadView,
            enableEmojiPicker,
            channelType,
            teamName,
            useChannelMentions,
            useCustomGroupMentions,
            useLDAPGroupMentions,
            badConnection,
            maxPostSize,
        } = this.props;
        const readOnlyChannel = !canPost;
        const {formatMessage} = this.props.intl;
        const enableAddButton = this.shouldEnableAddButton();
        const {
            serverError,
            clientUploadError,
            postError,
            fileInfos,
            uploadsInProgress,
            message,
            uploadsProgressPercent,
            showEmojiPicker,
            renderScrollbar,
            scrollbarWidth,
            quote,
        } = this.state;

        const ariaLabelReplyInput = Utils.localizeMessage('accessibility.sections.rhsFooter', 'reply input region');

        let errorContent = null;
        const error = serverError || clientUploadError;
        if (error) {
            errorContent = (
                <MessageSubmitError
                    error={error}
                    submittedMessage={serverError?.submittedMessage}
                    handleSubmit={this.handleSubmit}
                />
            );
        }

        let postErrorContent = null;
        if (postError) {
            postErrorContent = <TextboxError error={postError} />;
        }

        let preview = null;

        if (!readOnlyChannel && (fileInfos.length > 0 || uploadsInProgress.length > 0)) {
            preview = (
                <FilePreview
                    fileInfos={fileInfos}
                    onRemove={this.removePreview}
                    uploadsInProgress={uploadsInProgress}
                    uploadsProgressPercent={uploadsProgressPercent}
                />
            );
        }

        let uploadsInProgressText = null;

        if (uploadsInProgress.length > 0) {
            uploadsInProgressText = (
                <span className='post-right-comments-upload-in-progress'>
                    {uploadsInProgress.length === 1 ? (
                        <FormattedMessage
                            id='create_comment.file'
                            defaultMessage='File uploading'
                        />
                    ) : (
                        <FormattedMessage
                            id='create_comment.files'
                            defaultMessage='Files uploading'
                        />
                    )}
                </span>
            );
        }

        let fileUpload;

        if (!readOnlyChannel && !shouldShowPreview) {
            fileUpload = (
                <FileUpload
                    ref={this.fileUploadRef}
                    fileCount={this.getFileCount()}
                    getTarget={this.getFileUploadTarget}
                    onFileUploadChange={this.handleFileUploadChange}
                    onUploadStart={this.handleUploadStart}
                    onFileUpload={this.handleFileUploadComplete}
                    onUploadServerError={this.handleUploadServerError}
                    onUploadClientError={this.handleUploadClientError}
                    onUploadProgress={this.handleUploadProgress}
                    rootId={rootId}
                    channelId={channelId}
                    postType={isThreadView ? 'thread' : 'comment'}
                />
            );
        }

        let emojiPicker = null;

        const emojiButtonAriaLabel = formatMessage({
            id: 'emoji_picker.emojiPicker',
            defaultMessage: 'Emoji Picker',
        }).toLowerCase();

        if (enableEmojiPicker && !readOnlyChannel && !shouldShowPreview) {
            emojiPicker = (
                <div>
                    <EmojiPickerOverlay
                        show={showEmojiPicker}
                        target={this.getCreateCommentControls}
                        onHide={this.hideEmojiPicker}
                        onEmojiClick={this.handleEmojiClick}
                        topOffset={-5}
                    />
                    <button
                        aria-label={emojiButtonAriaLabel}
                        type='button'
                        onClick={this.toggleEmojiPicker}
                        className={classNames(
                            'emoji-picker__container',
                            'post-action',
                            showEmojiPicker && 'post-action--active',
                        )}
                    >
                        <EmojiIcon className={'icon icon--emoji emoji-rhs '} />
                    </button>
                </div>
            );
        }

        let moreButton;

        if (!readOnlyChannel) {
            moreButton = <CreateCommentMoreButton onPreviewExit={this.focusTextbox} />;
        }

        const sendButtonTooltip = (
            <Tooltip id='helpButtonTooltip'>
                <div aria-hidden={true}>
                    {formatMessage({
                        id: 'create_comment.comment',
                        defaultMessage: 'Reply',
                    })}
                </div>
            </Tooltip>
        );

        const sendButton = (
            <div>
                <OverlayTrigger
                    delayShow={Constants.OVERLAY_TIME_DELAY}
                    placement='top'
                    overlay={sendButtonTooltip}
                >
                    <button
                        className='post-action send-button'
                        disabled={!enableAddButton}
                        id='addCommentButton'
                        onClick={this.handleSubmit}
                    >
                        <LocalizedIcon
                            className='fa fa-paper-plane'
                            title={{
                                id: t('create_post.icon'),
                                defaultMessage: 'Create a post',
                            }}
                        />
                    </button>
                </OverlayTrigger>
            </div>
        );

        let createMessage;

        if (readOnlyChannel) {
            createMessage = Utils.localizeMessage(
                'create_post.off',
                'The channel is read-only',
            );
        } else {
            createMessage = Utils.localizeMessage('create_comment.addComment', 'Reply to this thread...');
        }

        return (
            <form onSubmit={this.handleSubmit} className={styles.form}>
                <MsgTyping
                    channelId={channelId}
                    postId={rootId}
                />
                <div
                    role='form'
                    aria-label={ariaLabelReplyInput}
                    tabIndex={-1}
                    className={classNames('post-create a11y__region', renderScrollbar && 'scroll')}
                    style={
                        renderScrollbar && scrollbarWidth ? {
                            ['--detected-scrollbar-width' as any]: `${this.state.scrollbarWidth}px`,
                        } : undefined
                    }
                    data-a11y-sort-order='4'
                >
                    {quote ? (
                        <div className='post-create__quote'>
                            <PostQuote
                                post={quote}
                                isPreview={true}
                                channelType={channelType}
                                teamName={teamName}
                                onClose={this.handlePostQuoteClose}
                            />
                        </div>
                    ) : null}
                    <div
                        id={rootId}
                        className='post-create-body comment-create-body'
                    >
                        <div className='post-body__cell'>
                            <Textbox
                                onChange={this.handleChange}
                                onKeyPress={this.commentMsgKeyPress}
                                onKeyDown={this.handleKeyDown}
                                onSelect={this.handleSelect}
                                onMouseUp={this.handleMouseUpKeyUp}
                                onKeyUp={this.handleMouseUpKeyUp}
                                onComposition={this.emitTypingEvent}
                                onHeightChange={this.handleHeightChange}
                                handlePostError={this.handlePostError}
                                value={readOnlyChannel ? '' : message}
                                onBlur={this.handleBlur}
                                createMessage={createMessage}
                                emojiEnabled={enableEmojiPicker}
                                channelId={channelId}
                                rootId={rootId}
                                isRHS={true}
                                id='reply_textbox'
                                ref={this.textboxRef}
                                disabled={readOnlyChannel}
                                characterLimit={maxPostSize}
                                preview={shouldShowPreview}
                                suggestionList={RhsSuggestionList}
                                badConnection={badConnection}
                                listenForMentionKeyClick={true}
                                useChannelMentions={useChannelMentions}
                                useGroupMentions={useCustomGroupMentions || useLDAPGroupMentions}
                            />
                            <span
                                ref={this.createCommentControlsRef}
                                className='post-body__actions'
                            >
                                {fileUpload}
                                {emojiPicker}
                                {moreButton}
                                {sendButton}
                            </span>
                        </div>
                    </div>
                    <div
                        id='commentCreateFooter'
                        role='form'
                        className={classNames('post-create-footer', postErrorContent && 'has-error')}
                    >
                        {postErrorContent}
                        {uploadsInProgressText}
                        {preview}
                        {errorContent}
                    </div>
                </div>
            </form>
        );
    }
}

export default injectIntl(CreateComment, {forwardRef: true});
